import { writable } from 'svelte/store';
import { assets } from './assets';
import type { EventEntry } from './backend';
import { enumIs } from './enums';
import type { ClientGame, Renderer } from './game';
import { unreachable } from './unreachable';

type Step =
  | 'click-the-canvas'
  | 'move-around'
  | 'find-a-weapon'
  | 'pick-up-a-weapon'
  | 'go-in-a-brush'
  | 'attack-an-enemy';

type Marker =
  | {
      type: 'POI';
      x: number;
      y: number;
    }
  | {
      type: 'HudRegion';
      x: number;
      y: number;
      width: number;
      height: number;
    };

export class Guide {
  step: Step = 'click-the-canvas';

  constructor() {
    this.load();
  }

  clear() {
    localStorage.removeItem('guide');
    this.step = 'click-the-canvas';
    this.save();
  }

  canvasClicked() {
    if (this.step === 'click-the-canvas') {
      this.step = 'move-around';
      this.save();
    }
  }

  load() {
    const stored = localStorage.getItem('guide');
    if (stored) {
      this.step = stored as Step;
    }
  }

  save() {
    localStorage.setItem('guide', this.step);
  }

  advance(game: ClientGame, events: EventEntry[], renderer: Renderer) {
    switch (this.step) {
      case 'click-the-canvas':
        break;
      case 'move-around':
        if (
          events.find(
            (e) =>
              enumIs(e.data, 'Player') &&
              enumIs(e.data.Player.event, 'PlayerMoved') &&
              e.data.Player.player.toText() == game.ownPlayer.principalText &&
              game.ownPlayer.queue.getQueue().length > 2
          )
        ) {
          this.step = 'find-a-weapon';
          this.save();
        }

        break;

      case 'find-a-weapon': {
        const tile =
          renderer.map.tiles[Math.floor(game.ownPlayer.loc.y)][
            Math.floor(game.ownPlayer.loc.x)
          ];
        if (enumIs(tile, 'Indoors') || enumIs(tile, 'HouseDoor')) {
          this.step = 'pick-up-a-weapon';
          this.save();
        }
        break;
      }

      case 'pick-up-a-weapon':
        if (game.ownPlayer.weapon) {
          this.step = 'go-in-a-brush';
          this.save();
        }
        break;

      case 'go-in-a-brush': {
        if (
          events.find(
            (e) =>
              enumIs(e.data, 'Player') &&
              enumIs(e.data.Player.event, 'PlayerMoved') &&
              e.data.Player.player.toText() != game.ownPlayer.principalText
          )
        ) {
          this.step = 'attack-an-enemy';
          this.save();
        }

        break;
      }
    }
  }

  /*
    Draw guide in the bottom left corner, on a 200x200 white rectangle

    The rect should have top header with the name "Guide"

    Inside the rect there should be the name of the Step, and a paragraph about what to do next.

    Some steps can include an image
  */
  draw(ctx: CanvasRenderingContext2D, game: ClientGame, renderer: Renderer) {
    let markers: Marker[] = [];
    switch (this.step) {
      case 'click-the-canvas':
        this.drawClickTheCanvas(ctx, game);
        break;
      case 'move-around':
        this.drawMoveAround(ctx, game);
        break;
      case 'find-a-weapon':
        markers.push(...this.drawFindAWeapon(ctx, game));
        break;

      case 'pick-up-a-weapon':
        markers.push(...this.drawPickUpWeapon(ctx, renderer));
        break;

      case 'go-in-a-brush':
        markers.push(...this.drawGoToABrush(ctx, renderer));
        break;

      case 'attack-an-enemy':
        this.killTheEnemy(ctx, renderer);
        break;
    }

    for (const marker of markers) {
      if (marker.type === 'POI') {
        const canvasCoords = renderer.toCanvasCoords(marker.x, marker.y);

        // draw assets.images.flag at coords
        ctx.drawImage(
          assets.images.flag,
          canvasCoords.x - renderer.tileSize / 2,
          canvasCoords.y - renderer.tileSize,
          renderer.tileSize,
          renderer.tileSize
        );
      } else if (marker.type === 'HudRegion') {
        // draw red rectangle

        ctx.strokeStyle = 'rgba(255,0,0,0.5)';
        ctx.lineWidth = 2;
        ctx.strokeRect(marker.x, marker.y, marker.width, marker.height);
      } else {
        unreachable(marker);
      }
    }
  }

  private static drawMultiLineText(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    lineHeight: number
  ) {
    const lines = text.split('\n');
    lines.forEach((line, i) => {
      ctx.fillText(line, x, y + i * lineHeight);
    });
  }

  private static drawBox(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    margin: number,
    padding: number,
    title: string,
    text: string
  ) {
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.fillRect(x, y, width, height);

    ctx.fillStyle = 'black';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.font = 'bold 16px sans-serif';
    ctx.fillText(title, x + width / 2, y + padding);

    ctx.font = '16px sans-serif';
    Guide.drawMultiLineText(ctx, text, x + width / 2, y + 50, 20);
  }

  private drawClickTheCanvas(ctx: CanvasRenderingContext2D, game: ClientGame) {
    const width = 300,
      height = 120,
      margin = 20,
      padding = 20,
      x = ctx.canvas.width / 2 - width / 2,
      y = ctx.canvas.height / 2 - height - margin;

    Guide.drawBox(
      ctx,
      x,
      y,
      width,
      height,
      margin,
      padding,
      'Welcome to the game!',
      'Click on the screen to\nstart moving your character.'
    );
  }

  private drawMoveAround(ctx: CanvasRenderingContext2D, game: ClientGame) {
    const width = 300,
      height = 140,
      margin = 20,
      padding = 20,
      x = ctx.canvas.width / 2 - width / 2,
      y = ctx.canvas.height - 120 - height;

    Guide.drawBox(
      ctx,
      x,
      y,
      width,
      height,
      margin,
      padding,
      'Move around!',
      `Click around to start moving.\nPlan your path ahead to move\nefficiently.`
    );
  }

  private drawFindAWeapon(
    ctx: CanvasRenderingContext2D,
    game: ClientGame
  ): Marker[] {
    const markers: Marker[] = [];

    const width = 300,
      height = 150,
      margin = 20,
      padding = 20,
      x = ctx.canvas.width / 2 - width / 2,
      y = ctx.canvas.height - 120 - height;

    Guide.drawBox(
      ctx,
      x,
      y,
      width,
      height,
      margin,
      padding,
      'Find a weapon!',
      `Very good!\nNow go find a weapon as soon as\npossible. A pistol or a knife will do.\nYou can find one in a house.`
    );

    markers.push({
      type: 'POI',
      x: 36,
      y: 10,
    });

    return markers;
  }

  private drawPickUpWeapon(
    ctx: CanvasRenderingContext2D,
    renderer: Renderer
  ): Marker[] {
    const markers: Marker[] = [];

    const width = 300,
      height = 150,
      margin = 20,
      padding = 20,
      x = ctx.canvas.width / 2 - width / 2,
      y = ctx.canvas.height - 120 - height;

    Guide.drawBox(
      ctx,
      x,
      y,
      width,
      height,
      margin,
      padding,
      'Pick up the pistol!',
      `Quickly! Pick up the pistol and\nget out of there!`
    );

    const canvasCoords = renderer.toCanvasCoords(34, 9);

    markers.push({
      type: 'HudRegion',
      x: canvasCoords.x - (renderer.tileSize * 1.5) / 2,
      y: canvasCoords.y - (renderer.tileSize * 1.5) / 2,
      width: renderer.tileSize * 1.5,
      height: renderer.tileSize * 1.5,
    });

    return markers;
  }

  private drawGoToABrush(
    ctx: CanvasRenderingContext2D,
    renderer: Renderer
  ): Marker[] {
    const markers: Marker[] = [];

    const width = 300,
      height = 150,
      margin = 20,
      padding = 20,
      x = ctx.canvas.width / 2 - width / 2,
      y = ctx.canvas.height - 120 - height;

    Guide.drawBox(
      ctx,
      x,
      y,
      width,
      height,
      margin,
      padding,
      'Hide in a brush',
      `Go hide in a brush and wait for\nan enemy to come by. As long as\nyou are in a brush, enemies outside\ncannot see you.`
    );

    markers.push({
      type: 'POI',
      x: 38,
      y: 30,
    });

    return markers;
  }

  private killTheEnemy(ctx: CanvasRenderingContext2D, renderer: Renderer) {
    const width = 300,
      height = 55,
      margin = 20,
      padding = 20,
      x = ctx.canvas.width - padding - width,
      y = padding;

    Guide.drawBox(
      ctx,
      x,
      y,
      width,
      height,
      margin,
      padding,
      'Kill the enemy!',
      ``
    );
  }
}

export const guide = writable(new Guide());
